import { Controller } from "@hotwired/stimulus"
import { creationOptions, requestOptions, serializeCredential } from "../src/passkeys"

export default class extends Controller {
  static targets = ["button", "error", "name", "password", "registration"]
  static values = {
    optionsUrl: String,
    submitUrl: String,
    unsupported: String,
    failure: String,
    cancelled: String
  }

  connect() {
    this.busy = false
    this.supported = window.isSecureContext && !!window.PublicKeyCredential &&
      typeof navigator.credentials?.create === "function" && typeof navigator.credentials?.get === "function"
    this.registrationTargets.forEach(element => { element.hidden = !this.supported })

    if (!this.supported) {
      this.buttonTargets.forEach(button => { button.disabled = true })
      this.showError(this.unsupportedValue)
    }
  }

  disconnect() {
    this.abortController?.abort()
  }

  authenticate(event) {
    event.preventDefault()
    this.perform("get")
  }

  register(event) {
    event.preventDefault()
    if (event.target.reportValidity()) this.perform("create")
  }

  async perform(operation) {
    if (this.busy || !this.supported) return

    this.busy = true
    this.errorTarget.hidden = true
    this.buttonTargets.forEach(button => { button.disabled = true })
    this.abortController = new AbortController()

    try {
      const payload = operation === "create" ? { password: this.passwordTarget.value } : {}
      const options = await this.post(this.optionsUrlValue, payload)
      const publicKey = operation === "create" ? creationOptions(options) : requestOptions(options)
      const credential = await navigator.credentials[operation]({ publicKey, signal: this.abortController.signal })
      if (!credential) throw new Error(this.cancelledValue)

      const submission = { credential: serializeCredential(credential) }
      if (operation === "create") submission.name = this.nameTarget.value

      const result = await this.post(this.submitUrlValue, submission)
      window.location.assign(result.redirect_url)
    } catch (error) {
      if (!this.element.isConnected) return

      const cancelled = error.name === "NotAllowedError" || error.name === "AbortError"
      this.showError(cancelled ? this.cancelledValue : error.name === "Error" ? error.message : this.failureValue)
    } finally {
      this.busy = false
      this.buttonTargets.forEach(button => { button.disabled = false })
    }
  }

  async post(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      signal: this.abortController.signal,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify(payload)
    })

    if (response.redirected || !response.headers.get("content-type")?.includes("application/json")) {
      throw new Error(this.failureValue)
    }

    const result = await response.json()
    if (!response.ok) throw new Error(result.error || this.failureValue)

    return result
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }
}
